import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Paper,
    Slider,
    // Grid, // Grid was not used in the main layout of questions
    Container,
    FormHelperText,
    FormControl,
    FormLabel,
    Stack
} from '@mui/material';
import api from './api'; 
import config from './config';

// Define the Likert scale marks and values
const charismaLikertMarks = [
    { value: -10, label: <Typography variant="caption" sx={{ textAlign: 'center', lineHeight: '1.2', display: 'block', width: '70px' }}>Strongly<br/>Disagree</Typography> },
    { value: -5,  label: <Typography variant="caption">Disagree</Typography> },
    { value: 0,   label: <Typography variant="caption" sx={{ textAlign: 'center', lineHeight: '1.2', display: 'block', width: '70px' }}>Neither Agree<br/>nor Disagree</Typography> },
    { value: 5,   label: <Typography variant="caption">Agree</Typography> },
    { value: 10,  label: <Typography variant="caption" sx={{ textAlign: 'center', lineHeight: '1.2', display: 'block', width: '70px' }}>Strongly<br/>Agree</Typography> }
];

function CharismaDiagnosticSurvey({ onCharismaComplete, cohortId }) {
    const navigate = useNavigate();
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [responses, setResponses] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    // Fetch questions on component mount
    useEffect(() => {
        const fetchQuestions = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await api.get(`${config.backendUrl}/api/charisma/questions`);
                if (response.data && Array.isArray(response.data)) {
                    setQuestions(response.data);
                    // Initialize responses with default value (0 for "Neither Agree nor Disagree")
                    const initialResponses = {};
                    response.data.forEach(q => {
                        if (q && typeof q.question_id !== 'undefined') {
                            initialResponses[q.question_id] = 0; // Default to 0
                        }
                    });
                    setResponses(initialResponses);
                } else {
                    throw new Error("Invalid data format for Charisma Diagnostic questions.");
                }
            } catch (err) {
                console.error("Error fetching Charisma Diagnostic questions:", err);
                setError(err.response?.data?.message || err.message || "Failed to load survey questions.");
            } finally {
                setLoading(false);
            }
        };
        fetchQuestions();
    }, []);

    const handleResponseChange = useCallback((questionId, value) => {
        setResponses(prevResponses => ({
            ...prevResponses,
            [questionId]: value
        }));
        // Clear validation error for this question upon change
        if (validationErrors[questionId]) {
            setValidationErrors(prevErrors => {
                const nextErrors = { ...prevErrors };
                delete nextErrors[questionId];
                return nextErrors;
            });
        }
    }, [validationErrors]);

    const validateResponses = () => {
        const errors = {};
        let allAnswered = true;
        questions.forEach(q => {
            if (responses[q.question_id] === undefined || responses[q.question_id] === null) { 
                errors[q.question_id] = "Please select an answer for this statement.";
                allAnswered = false;
            }
        });
        setValidationErrors(errors);
        return allAnswered;
    };

    const handleSubmit = async () => {
        if (!validateResponses()) {
            const firstErrorKey = Object.keys(validationErrors)[0];
            if (firstErrorKey) {
                const errorElement = document.getElementById(`question-control-${firstErrorKey}`);
                if (errorElement) {
                    errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }
            return;
        }

        setIsSubmitting(true);
        setError(null);

        const formattedResponses = Object.entries(responses).map(([questionId, value]) => ({
            question_id: parseInt(questionId),
            response_value: value 
        }));

        try {
            await api.post(`${config.backendUrl}/api/charisma/responses`, {
                cohort_id: cohortId,
                responses: formattedResponses
            });
            await api.post(`${config.backendUrl}/api/charisma/complete`, {
                cohort_id: cohortId
            });
            
            onCharismaComplete(); 
            navigate('/dashboard', { state: { message: 'Charisma Diagnostic submitted successfully!' } });

        } catch (err) {
            console.error("Error submitting Charisma Diagnostic:", err);
            setError(err.response?.data?.message || err.message || "Failed to submit responses.");
        } finally {
            setIsSubmitting(false); // Ensure this is always set
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
    }
    if (error) {
        return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
    }
    if (!questions.length) {
        return <Alert severity="info" sx={{ m: 2 }}>No survey questions are currently available.</Alert>;
    }

    return (
        <Container maxWidth="md" sx={{ py: 3 }}>
            <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h4" component="h1" gutterBottom align="center">
                    Charisma Diagnostic
                </Typography>
                <Typography variant="body1" paragraph sx={{ textAlign: 'left', mb: 3 }}> {/* Changed from 'center' to 'left' */}
                    For each statement below, please indicate your level of agreement. 
                    Your responses will help identify your unique blend of warmth and confidence.
                </Typography>

                <Stack spacing={3}> {/* Adjusted spacing slightly if needed */}
                    {questions.map((question, index) => (
                        <FormControl 
                            key={question.question_id} 
                            component="fieldset" 
                            error={!!validationErrors[question.question_id]}
                            sx={{ 
                                width: '100%', 
                                // mb: 2, // mb is handled by Stack spacing
                                p: 1, // Reduced padding slightly
                                border: validationErrors[question.question_id] ? '1px solid red' : '1px solid #e0e0e0', 
                                borderRadius: '4px' 
                            }}
                            id={`question-control-${question.question_id}`}
                        >
                            <FormLabel component="legend" sx={{ textAlign: 'left', width: '100%', mb: 1, fontWeight: 'medium', color: 'text.primary', whiteSpace: 'normal' }}> {/* Added whiteSpace: 'normal' for explicit wrapping */}
                                {index + 1}. {question.question_text}
                            </FormLabel>
                            <Box sx={{ width: {xs: '95%', sm: '90%'}, mx: 'auto', mt: 1, mb: {xs: 7, sm: 6} }}> {/* Adjusted mb and width for responsiveness */}
                                <Slider
                                    value={responses[question.question_id] || 0}
                                    onChange={(e, newValue) => handleResponseChange(question.question_id, newValue)}
                                    aria-labelledby={`slider-label-${question.question_id}`}
                                    step={null} 
                                    marks={charismaLikertMarks}
                                    min={-10}
                                    max={10}
                                    valueLabelDisplay="off"
                                    sx={{
                                        '& .MuiSlider-markLabel': { 
                                            fontSize: {xs: '0.65rem', sm: '0.75rem'}, 
                                            whiteSpace: 'normal',
                                        },
                                        '& .MuiSlider-thumb': { 
                                            height: {xs: 18, sm: 20},
                                            width: {xs: 18, sm: 20},
                                            backgroundColor: '#fff',
                                            border: '2px solid currentColor',
                                            '&:focus, &:hover, &.Mui-active': {
                                                boxShadow: '0px 0px 0px 8px rgba(25, 118, 210, 0.16)',
                                            },
                                        },
                                        '& .MuiSlider-track': { height: {xs: 5, sm:6}},
                                        '& .MuiSlider-rail': { height: {xs: 5, sm:6}}
                                    }}
                                />
                            </Box>
                            {validationErrors[question.question_id] && (
                                <FormHelperText sx={{ textAlign: 'left', marginLeft: '14px', mt: -4 }}>
                                    {validationErrors[question.question_id]}
                                </FormHelperText>
                            )}
                        </FormControl>
                    ))}
                </Stack>

                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        size="large"
                    >
                        {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Submit Diagnostic'}
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
}

export default CharismaDiagnosticSurvey;
